import { lookupsJson } from '@ourbranch/lookups';

/**
 * The Reason options for a non-rated individual. CFM auto (#35990) uses a dedicated, restricted list
 * — Excluded / Underage only — kept separate from the shared removedDriverReasonOptions so the two
 * CFM reasons never leak into the other (customer + staff) remove-driver dropdowns. Every other brand
 * keeps the full removedDriverReasonOptions list and, per the caller, some or all of the
 * nonRatedIndividualOptions.
 *
 * @param {object} opts
 * @param {boolean} opts.restrictToCfm - CFM auto: show only Excluded / Underage.
 * @param {'all'|'policyOnly'|'none'} opts.extra - which nonRatedIndividualOptions to append (non-CFM).
 */
export const nonRatedIndividualReasonOptions = ({ restrictToCfm = false, extra = 'all' } = {}) => {
  const { removedDriverReasonOptions, nonRatedIndividualOptions, cfmNonRatedIndividualReasonOptions } = lookupsJson;
  if (restrictToCfm) {
    return cfmNonRatedIndividualReasonOptions;
  }
  if (extra === 'none') {
    return removedDriverReasonOptions;
  }
  if (extra === 'policyOnly') {
    return removedDriverReasonOptions.concat(nonRatedIndividualOptions.filter((option) => option.policyOnly));
  }
  return removedDriverReasonOptions.concat(nonRatedIndividualOptions);
};

// Every reason lookup, used to resolve a label and to keep a stored value visible even when it's
// outside the active (CFM-restricted) list.
const ALL_REASON_LOOKUP_KEYS = [
  'removedDriverReasonOptions',
  'nonRatedIndividualOptions',
  'cfmNonRatedIndividualReasonOptions'
];

/**
 * Resolve a removedReason id to its display label across ALL reason lookups (#35990). Used for the
 * read-only Reason line so a CFM non-rated individual carrying a legacy/automation reason (e.g.
 * PRIOR_OWNER, PENDING_VERIFICATION) — which isn't in the restricted Excluded/Underage list — still
 * shows its real reason instead of a blank. Falls back to the raw id so the value is never empty.
 */
export const resolveNonRatedIndividualReasonLabel = (id) => {
  if (!id) {
    return undefined;
  }
  for (const key of ALL_REASON_LOOKUP_KEYS) {
    const found = (lookupsJson[key] || []).find((option) => option.id === id);
    if (found) {
      return found.value;
    }
  }
  return id;
};

/**
 * Ensure a stored removedReason still renders in a select even when it's outside the active list —
 * an existing CFM non-rated individual with a legacy/automation reason. Appends it as a disabled
 * "legacy" option so the dropdown shows the real reason and staff can't silently re-save a blank.
 */
export const withCurrentReasonOption = (options, currentId) => {
  if (!currentId || options.some((option) => option.id === currentId)) {
    return options;
  }
  return options.concat({ id: currentId, value: resolveNonRatedIndividualReasonLabel(currentId), disabled: true });
};
